import { createSelector } from '@reduxjs/toolkit';

// Base selector
const selectSolutions = state => state.solutions;

// Select solutions for a specific post
export const selectSolutionsByPostId = createSelector(
  [selectSolutions, (_, postId) => postId],
  (solutions, postId) => postId ? (solutions.byPostId[postId]?.solutions || []) : []
);

// Select loading state for a post's solutions
export const selectSolutionsLoading = createSelector(
  [selectSolutions, (_, postId) => postId],
  (solutions, postId) => postId ? (solutions.byPostId[postId]?.loading || false) : false
);

// Select error state for a post's solutions
export const selectSolutionsError = createSelector(
  [selectSolutions, (_, postId) => postId],
  (solutions, postId) => postId ? (solutions.byPostId[postId]?.error || null) : null
);

// Select pagination flags per post
export const selectSolutionsHasMore = createSelector(
  [selectSolutions, (_, postId) => postId],
  (solutions, postId) => postId ? Boolean(solutions.byPostId[postId]?.hasMore) : false
);

export const selectSolutionsLoadingMore = createSelector(
  [selectSolutions, (_, postId) => postId],
  (solutions, postId) => postId ? Boolean(solutions.byPostId[postId]?.loadingMore) : false
);

// Select total count for a post's solutions (independent of pagination)
export const selectSolutionsTotalCount = createSelector(
  [selectSolutions, (_, postId) => postId],
  (solutions, postId) => {
    if (!postId) return 0;
    const total = solutions.byPostId[postId]?.total;
    if (typeof total === 'number') return total;
    // Fallback to currently loaded length if total not yet set
    return solutions.byPostId[postId]?.solutions?.length || 0;
  }
);

// Current solution selectors
export const selectCurrentSolution = state => state.solutions.current.solution;
export const selectCurrentSolutionLoading = state => state.solutions.current.loading;
export const selectCurrentSolutionError = state => state.solutions.current.error;

// Check if user has upvoted a solution
export const selectHasUserUpvotedSolution = createSelector(
  [selectSolutions, (_, { solutionId, userId }) => ({ solutionId, userId })],
  (solutions, { solutionId, userId }) => {
    if (!solutionId || !userId) return false;
    
    // Check current solution first
    if (solutions.current.solution?.id === solutionId) {
      return solutions.current.solution.voters?.includes(userId) || false;
    }
    
    // Check in all posts' solutions
    for (const postId in solutions.byPostId) {
      const solution = solutions.byPostId[postId]?.solutions?.find(s => s.id === solutionId);
      if (solution) {
        return solution.voters?.includes(userId) || false;
      }
    }
    
    return false;
  }
);

// Select builder state
export const selectSolutionBuilder = createSelector(
  [
    state => state.solutions.builder,
    state => state.solutions.builder.phases
  ],
  (builder, phases) => {
    return {
      solution: builder.solution || { title: '', type: '', description: '' },
      phases: phases || [],
      loading: builder.loading,
      error: builder.error
    };
  }
);

/**
 * Select solution by ID from any post
 */
export const selectSolutionById = createSelector(
  [
    selectSolutions,
    (_, { solutionId }) => solutionId
  ],
  (solutions, solutionId) => {
    if (!solutionId) return null;
    
    // Check current solution first
    if (solutions.current.solution?.id === solutionId) {
      return solutions.current.solution;
    }
    
    // Check all posts' solutions
    for (const postId in solutions.byPostId) {
      const solution = solutions.byPostId[postId].solutions?.find(s => s.id === solutionId);
      if (solution) return solution;
    }
    
    return null;
  }
);

// Saved solutions selectors
export const selectSavedSolutions = createSelector(
  [selectSolutions],
  (solutions) => solutions.saved.solutions || []
);

export const selectSavedSolutionsLoading = createSelector(
  [selectSolutions],
  (solutions) => solutions.saved.loading || false
);

export const selectSavedSolutionsError = createSelector(
  [selectSolutions],
  (solutions) => solutions.saved.error || null
);
